import * as Yup from "yup";
import {
  getPostcodeError,
  isValidPostcode,
} from "../../../utils/postcodeUtils";

const Form5 = Yup.object({
  campaignName: Yup.string().optional(),
  jobTitle: Yup.string().required("Job title is required"),
  jobDescription: Yup.string().required("Job description is required"),
  yearsOfExperience: Yup.number()
    .required("Years of experience is required")
    .min(0, "Years of experience cannot be negative")
    .max(40, "Years of experience cannot exceed 40"),
  employmentType: Yup.string().required("Employment type is required"),
  fromWorkingHours: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value
    )
    .min(0, "Hours must be between 0 and 168")
    .max(168, "Hours must be between 0 and 168")
    .optional(),
  fromWorkingMinutes: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value
    )
    .min(0, "Minutes must be between 0 and 60")
    .max(60, "Minutes must be between 0 and 60")
    .optional(),
  toWorkingHours: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value
    )
    .min(0, "Hours must be between 0 and 168")
    .max(168, "Hours must be between 0 and 168")
    .optional(),
  toWorkingMinutes: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value
    )
    .min(0, "Minutes must be between 0 and 60")
    .max(60, "Minutes must be between 0 and 60")
    .optional(),
  // Salary group fields (validated together via an object-level test below)
  salaryIndicationFrom: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value
    )
    .min(0, "Must be >= 0")
    .notRequired(),
  salaryIndicationTo: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? 0 : value
    )
    .min(0, "Must be >= 0")
    .when("salaryIndicationFrom", (from: any, schema: any) => {
      const fromValue = from !== undefined && from !== null && from !== "" ? Number(from) : null;
      return schema.test(
        "to-greater-than-from",
        "The maximum salary must be higher than the minimum salary.",
        function (toValue: any) {
          const to = toValue !== undefined && toValue !== null && toValue !== "" ? Number(toValue) : 0;
          // If "To" is 0, it's allowed (default scenario)
          if (to === 0) return true;
          // If "From" is not provided, "To" can be any value >= 0
          if (fromValue === null) return true;
          // "To" must be greater than "From"
          return to > fromValue;
        }
      );
    })
    .notRequired(),
  currency: Yup.string().optional(),
  period: Yup.string().optional(),
  addressLine1: Yup.string().required("Address line 1 is required"),
  addressLine2: Yup.string().optional(),
  postCode: Yup.string()
    .required("Post code is required")
    .test("postcode-format", "Invalid postcode", function (value) {
      const country = this.parent.country as string | undefined;
      if (!isValidPostcode(value || "", country)) {
        return this.createError({ message: getPostcodeError(country) });
      }
      return true;
    }),

  // ✅ City validation (only letters & spaces)
  city: Yup.string()
    .required("City is required")
    .matches(/^[A-Za-z\s]+$/, "City must contain only letters"),
  country: Yup.string()
    .required("Country is required")
    .matches(/^[A-Za-z\s]+$/, "Country must contain only letters"),
}).test(
  "salary-group-all-or-none",
  "All salary fields are required",
  function (value) {
    const v: any = value || {};
    // Currency is optional, so exclude it from the all-or-none check
    const fields = [
      { key: "salaryIndicationFrom", val: v.salaryIndicationFrom },
      { key: "salaryIndicationTo", val: v.salaryIndicationTo },
    ];
    
    // Check if any salary field is filled (excluding "To" if it's 0)
    const fromFilled = fields[0].val !== undefined && fields[0].val !== null && fields[0].val !== "";
    const toFilled = fields[1].val !== undefined && fields[1].val !== null && fields[1].val !== "" && fields[1].val !== "0";
    
    // If no salary fields are filled, validation passes (Period will be optional)
    if (!fromFilled && !toFilled) {
      return true;
    }
    
    // If salary fields are filled, check if both From and To are provided
    const filledCount = [fromFilled, toFilled].filter(Boolean).length;
    
    // If both salary fields are filled, validation passes (Period requirement is handled by the when() condition)
    if (filledCount === 2) {
      return true;
    }
    
    // If only one salary field is filled, show error on the missing one
    if (!fromFilled) {
      return this.createError({
        path: "salaryIndicationFrom",
        message: "Salary From is required when salary information is provided",
      });
    }
    
    // This shouldn't happen since "To" defaults to 0, but just in case
    return true;
  }
);

export default Form5;
